// src/components/Dashboard/charts/SLAUserComplianceChart.tsx
import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface SLAUserComplianceChartProps {
  userData: any;
}

const SLAUserComplianceChart: React.FC<SLAUserComplianceChartProps> = ({
  userData,
}) => {
  if (!userData || !userData.combined_compliance) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500">No hay datos disponibles</p>
      </div>
    );
  }

  // Filter users with actual data and prepare chart data
  const chartData = userData.combined_compliance
    .filter((user: any) => user.total_processed > 0)
    .map((user: any) => ({
      name: user.user_name,
      "Antes 1:00 PM": user.before_cutoff_pct,
      "Después 1:00 PM": user.after_cutoff_pct,
      General: user.compliance_pct,
      totalProcessed: user.total_processed,
      compliant: user.compliant,
      email: user.user_email,
    }));

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900">{label}</p>
          <p className="text-xs text-gray-600 mb-2">{data.email}</p>
          <div className="space-y-1">
            {payload.map((entry: any, index: number) => (
              <p key={index} className="text-sm" style={{ color: entry.color }}>
                {entry.dataKey}: {entry.value.toFixed(1)}%
              </p>
            ))}
            <div className="border-t pt-2 mt-2 text-xs text-gray-600">
              <p>Total Procesados: {data.totalProcessed}</p>
              <p>Cumplidos: {data.compliant}</p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500">No hay usuarios con datos procesados</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart
        data={chartData}
        margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
        barCategoryGap="20%"
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 12 }}
          interval={0}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis
          tick={{ fontSize: 12 }}
          domain={[0, 100]}
          tickFormatter={(value) => `${value}%`}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Bar
          dataKey="Antes 1:00 PM"
          fill="#AEF0AD"
          name="Antes 1:00 PM"
          radius={[2, 2, 0, 0]}
        />
        <Bar
          dataKey="Después 1:00 PM"
          fill="#D0E89E"
          name="Después 1:00 PM"
          radius={[2, 2, 0, 0]}
        />
        <Bar
          dataKey="General"
          fill="#809BE8"
          name="General"
          radius={[2, 2, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default SLAUserComplianceChart;
